import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input, Select } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import financialService from '@services/financialService';
import { checkingAccountService } from '@services/checkingAccountService';
import companyService from '@services/companyService';
import { CreditCardFormData, FinancialAccount } from '@/types/financial';
import { Bank } from '@/types/checkingAccount';
import { Company, formatCnpj } from '@/types/company';
import { buildCategoryOptions, CategoryOptionGroup } from '@pages/financial/financialUtils';
import { CategorySelect } from '@pages/financial/financialShared';
import { creditCardPayload, creditCardToForm, emptyCreditCardForm, validateCreditCardForm } from './creditCardForm';

const LIST_PATH = '/settings/financial/credit-cards';

/** Converte um campo de dia ('' → null). */
const toDay = (value: string): number | null => (value.trim() === '' ? null : Number(value));

export const CreditCardFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof CreditCardFormData, string>>>({});

  const [companies, setCompanies] = useState<Company[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [categoryGroups, setCategoryGroups] = useState<CategoryOptionGroup[]>([]);
  const [formData, setFormData] = useState<CreditCardFormData>(emptyCreditCardForm);

  useEffect(() => {
    Promise.all([companyService.getCompanies(), checkingAccountService.listBanks()])
      .then(([cs, bs]) => {
        setCompanies(cs);
        setBanks(bs);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar dados de apoio'));
  }, []);

  useEffect(() => {
    if (!isEditing || !id) return;
    setLoading(true);
    setError(null);
    financialService
      .getCreditCard(parseInt(id))
      .then((card) => setFormData(creditCardToForm(card)))
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar cartão'))
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  // Conta e categoria dependem da empresa.
  useEffect(() => {
    if (!formData.companyId) {
      setAccounts([]);
      setCategoryGroups([]);
      return;
    }
    checkingAccountService
      .getAll(formData.companyId)
      .then(setAccounts)
      .catch(() => setAccounts([]));
    financialService
      .getAccountingPlans(formData.companyId)
      .then((tree) => setCategoryGroups(buildCategoryOptions(tree)))
      .catch(() => setCategoryGroups([]));
  }, [formData.companyId]);

  const updateField = <K extends keyof CreditCardFormData>(field: K, value: CreditCardFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const changeCompany = (companyId: number) => {
    // A conta é da empresa: trocar a empresa obriga a escolher a conta de novo.
    setFormData((prev) => ({ ...prev, companyId, idAccountsMain: null }));
    setErrors((prev) => ({ ...prev, companyId: undefined }));
  };

  const categoryKnown =
    formData.accountingPlanErpId == null ||
    categoryGroups.length === 0 ||
    categoryGroups.some((g) => g.options.some((o) => o.value === formData.accountingPlanErpId));

  const handleSave = async () => {
    const found = validateCreditCardForm(formData);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      setSaving(true);
      setError(null);
      const payload = creditCardPayload(formData);
      if (isEditing && id) {
        await financialService.updateCreditCard(parseInt(id), payload);
      } else {
        await financialService.createCreditCard(payload);
      }
      navigate(LIST_PATH);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate(LIST_PATH)}>
            <ArrowLeft size={20} />
          </Button>
          <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate(LIST_PATH)}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">{isEditing ? 'Editar Cartão de Crédito' : 'Novo Cartão de Crédito'}</h1>
          <p className="text-slate-600">
            {isEditing ? 'Altere os dados do cartão' : 'Preencha os dados do novo cartão'}
          </p>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário */}
      <Card>
        <CardHeader title="Dados do Cartão" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Empresa */}
            <div className="md:col-span-2">
              <Select
                label="Empresa"
                required
                value={formData.companyId || ''}
                error={errors.companyId}
                onChange={(e) => changeCompany(Number(e.target.value))}
              >
                <option value="">Selecione a empresa</option>
                {companies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>
                    {formatCnpj(c.document)} — {c.name}
                  </option>
                ))}
              </Select>
              {isEditing && (
                <p className="text-xs text-slate-500 mt-1">
                  Depois da primeira fatura a empresa não pode mais ser alterada.
                </p>
              )}
            </div>

            {/* Descrição */}
            <div className="md:col-span-2">
              <Input
                label="Descrição"
                required
                value={formData.description}
                error={errors.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Ex: Visa Infinite 4626"
                maxLength={100}
                helperText="Identifique o cartão pela bandeira e pelos 4 últimos dígitos. Não digite o número completo."
              />
            </div>

            {/* Fechamento */}
            <div>
              <Input
                label="Dia de fechamento da fatura"
                required
                type="number"
                min={1}
                max={31}
                value={formData.billingCycleDay ?? ''}
                error={errors.billingCycleDay}
                onChange={(e) => updateField('billingCycleDay', toDay(e.target.value))}
                helperText="Compras depois deste dia caem na fatura do mês seguinte."
              />
            </div>

            {/* Vencimento */}
            <div>
              <Input
                label="Dia de vencimento da fatura"
                required
                type="number"
                min={1}
                max={31}
                value={formData.billingDueDay ?? ''}
                error={errors.billingDueDay}
                onChange={(e) => updateField('billingDueDay', toDay(e.target.value))}
              />
            </div>

            {/* Banco */}
            <div>
              <Select
                label="Banco emissor"
                value={formData.bankNumber ?? ''}
                onChange={(e) => updateField('bankNumber', e.target.value === '' ? null : e.target.value)}
              >
                <option value="">Não informado</option>
                {banks.map((b) => (
                  <option key={b.bankNumber} value={b.bankNumber}>
                    {b.bankNumber} — {b.bankName ?? b.bankDescription ?? ''}
                  </option>
                ))}
              </Select>
            </div>

            {/* Conta de pagamento */}
            <div>
              <Select
                label="Conta de pagamento da fatura"
                required
                value={formData.idAccountsMain ?? ''}
                error={errors.idAccountsMain}
                disabled={!formData.companyId}
                onChange={(e) => updateField('idAccountsMain', e.target.value === '' ? null : Number(e.target.value))}
              >
                <option value="">{formData.companyId ? 'Selecione a conta' : 'Selecione a empresa primeiro'}</option>
                {accounts.map((a) => (
                  <option key={a.idAccountsMain} value={a.idAccountsMain}>
                    {a.accountsDescription}
                  </option>
                ))}
              </Select>
            </div>

            {/* Categoria */}
            <div className="md:col-span-2">
              <label className="text-label block mb-1">
                Categoria da fatura <span className="text-red-500">*</span>
              </label>
              <CategorySelect
                groups={categoryGroups}
                value={formData.accountingPlanErpId}
                onChange={(v) => updateField('accountingPlanErpId', v)}
                disabled={!formData.companyId}
              />
              {errors.accountingPlanErpId && <p className="text-sm text-red-600 mt-1">{errors.accountingPlanErpId}</p>}
              {!categoryKnown && (
                <p className="text-sm text-amber-700 mt-1">
                  A categoria atual (código {formData.accountingPlanErpId}) não está no plano de contas desta empresa.
                  Ela continua gravada até você escolher outra.
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                A conta e a categoria são usadas no lançamento da fatura na conta corrente.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate(LIST_PATH)}>
          Cancelar
        </Button>
        <Button onClick={handleSave} loading={saving} icon={<Save size={18} />}>
          {isEditing ? 'Salvar Alterações' : 'Cadastrar Cartão'}
        </Button>
      </div>
    </div>
  );
};
